import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';

import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PanelModule } from 'primeng/panel';

import { AgentsService } from '@generated/services/agents.service';
import { SessionsService } from '@generated/services/sessions.service';
import { AgentSessionDto } from '@generated/models/agent-session-dto';
import { AgentSessionStatus } from '@generated/models/agent-session-status';
import { AgentSummary } from '@generated/models/agent-summary';
import { NotificationService } from '@core/services/notification.service';
import { BreakpointService } from '@core/services/breakpoint.service';
import { DESKTOP_ROW_HEIGHT, MOBILE_ROW_HEIGHT, LOAD_MORE_THRESHOLD } from '../conversation-list.constants';

@Component({
  selector: 'app-sessions-list',
  imports: [
    DatePipe,
    FormsModule,
    RouterLink,
    ScrollingModule,
    DatePickerModule,
    SelectModule,
    TagModule,
    ProgressSpinnerModule,
    PanelModule,
  ],
  templateUrl: './sessions-list.component.html',
  styleUrl: './sessions-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionsListComponent implements OnInit {
  private readonly agentsService = inject(AgentsService);
  private readonly sessionsService = inject(SessionsService);
  private readonly notificationService = inject(NotificationService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  /** Панель фильтров стартует свёрнутой на мобиле — иначе занимает экран целиком. */
  readonly breakpoints = inject(BreakpointService);

  readonly ROWS_PER_PAGE = 20;

  /** Высота строки для cdk-virtual-scroll — должна совпадать с CSS
   *  (.list-row height), иначе виртуальный скроллер промахивается. */
  readonly rowHeight = computed(() =>
    this.breakpoints.isMobile() ? MOBILE_ROW_HEIGHT : DESKTOP_ROW_HEIGHT
  );

  readonly dateAfter = signal<Date | null>(null);
  readonly dateBefore = signal<Date | null>(null);
  readonly selectedAgentId = signal<string | null>(null);
  readonly selectedStatus = signal<AgentSessionStatus | null>(null);

  readonly sessions = signal<AgentSessionDto[]>([]);
  readonly isLoading = signal<boolean>(false);
  private nextCursor: string | null = null;
  readonly hasMore = signal<boolean>(true);

  readonly statusOptions: { label: string; value: AgentSessionStatus }[] = [
    { label: $localize`:@@conversations.status.pending:Pending`, value: 'Pending' },
    { label: $localize`:@@conversations.status.dispatching:Dispatching`, value: 'Dispatching' },
    { label: $localize`:@@conversations.status.active:Active`, value: 'Active' },
    { label: $localize`:@@conversations.status.completed:Completed`, value: 'Completed' },
    { label: $localize`:@@conversations.status.failed:Failed`, value: 'Failed' },
    { label: $localize`:@@conversations.status.cancelled:Cancelled`, value: 'Cancelled' },
    { label: $localize`:@@conversations.status.timeout:Timeout`, value: 'Timeout' },
  ];

  readonly unknownAgentLabel = $localize`:@@conversations.unknownAgent:Unknown Agent`;
  readonly unknownStatusLabel = $localize`:@@conversations.status.unknown:Unknown`;

  readonly agents = signal<AgentSummary[]>([]);
  readonly agentOptions = computed(() => {
    const unnamed = $localize`:@@conversations.unnamedAgent:Unnamed Agent`;
    return this.agents()
      .map(a => ({ label: a.title || unnamed, value: a.id || '' }))
      .filter(o => o.value);
  });

  ngOnInit(): void {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        if (params['agentId']) this.selectedAgentId.set(params['agentId']);
        this.loadAgents();
        this.loadMore();
      });
  }

  private loadAgents(): void {
    this.agentsService.apiAgentsGet()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => this.agents.set(result.agents || []),
        error: () => this.notificationService.showError($localize`:@@conversations.notify.agentsFail:Failed to load agents`),
      });
  }

  loadMore(): void {
    if (this.isLoading()) return;
    if (this.sessions().length > 0 && !this.nextCursor) return;

    this.isLoading.set(true);
    this.sessionsService.apiSessionsGet({
      status: this.selectedStatus() || undefined,
      agentId: this.selectedAgentId() || undefined,
      dateFrom: this.dateFromIso,
      dateTo: this.dateToIso,
      cursor: this.nextCursor || undefined,
      limit: this.ROWS_PER_PAGE,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.sessions.update(prev => [...prev, ...(result.sessions || [])]);
          this.nextCursor = result.next_cursor || null;
          this.hasMore.set(!!this.nextCursor);
          this.isLoading.set(false);
        },
        error: () => {
          this.notificationService.showError($localize`:@@conversations.notify.sessionsFail:Failed to load sessions`);
          this.isLoading.set(false);
        },
      });
  }

  /**
   * Инфинит-скролл: cdk-virtual-scroll сообщает индекс первой видимой
   * строки, а сколько строк помещается в вьюпорт — считаем из его
   * высоты. Подгружаем следующую страницу, когда до конца загруженных
   * данных осталось меньше LOAD_MORE_THRESHOLD строк.
   */
  onScrolledIndexChange(firstVisibleIndex: number, viewportHeight: number): void {
    if (!this.nextCursor || this.isLoading()) return;
    const visibleCount = Math.ceil(viewportHeight / this.rowHeight());
    const lastVisibleIndex = firstVisibleIndex + visibleCount;
    if (lastVisibleIndex >= this.sessions().length - LOAD_MORE_THRESHOLD) {
      this.loadMore();
    }
  }

  onFilterChange(): void {
    this.sessions.set([]);
    this.nextCursor = null;
    this.hasMore.set(true);
    this.loadMore();
  }

  private get dateFromIso(): string | undefined {
    const d = this.dateAfter();
    return d ? d.toISOString() : undefined;
  }

  private get dateToIso(): string | undefined {
    const d = this.dateBefore();
    if (!d) return undefined;
    const end = new Date(d);
    end.setHours(23, 59, 59, 999);
    return end.toISOString();
  }

  getStatusLabel(status: AgentSessionStatus | null | undefined): string {
    if (!status) return this.unknownStatusLabel;
    return this.statusOptions.find(o => o.value === status)?.label ?? status;
  }

  getStatusSeverity(
    status: AgentSessionStatus | null | undefined,
  ): 'success' | 'danger' | 'warn' | 'info' | 'secondary' {
    switch (status) {
      case 'Completed': return 'success';
      case 'Active':
      case 'Dispatching': return 'info';
      case 'Failed': return 'danger';
      case 'Timeout': return 'warn';
      case 'Cancelled':
      case 'Pending':
      default: return 'secondary';
    }
  }

  formatDuration(seconds: number | null | undefined): string {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hours > 0) return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  trackById = (_index: number, session: AgentSessionDto): string | number => session?.id ?? _index;
}
